import { t } from "@lingui/macro";
import { useAtomValue } from "jotai";
import { Suspense, useEffect, useMemo, useState } from "react";
import { KeyboardAvoidingView, Platform, ScrollView } from "react-native";
import { useQueryLoader } from "react-relay";

import { Button } from "@whoosh/design-system/src/components/controls";
import UnderlinedTabs from "@whoosh/design-system/src/components/controls/UnderlinedTabs";
import { InfoBanner } from "@whoosh/design-system/src/components/display/InfoBanner/InfoBanner";
import { PersonPlusIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import { Box } from "@whoosh/design-system/src/components/primitive";

import { PlayersListSkeleton } from "../../AddPlayers";
import { currentAddPatronsSlotIdAtom } from "../atoms/stores";
import { useDebouncedSearch } from "../hooks/useDebouncedSearch";
import { AddPatronsInternalProps } from "../types";
import { AddPeopleSearchbox } from "./AddPeopleSearchBox";
import {
  CustomerList,
  CustomerListQueryString,
} from "./CustomerList/CustomerList";
import { CustomerListQuery } from "./CustomerList/__generated__/CustomerListQuery.graphql";
import { UnknownCustomerCounter } from "./CustomerList/components/UknownCustomerCounter";
import { GuestList, GuestListQueryString } from "./GuestList/GuestList";
import { GuestListHeader } from "./GuestList/GuestListHeader";
import { GuestListQuery } from "./GuestList/__generated__/GuestListQuery.graphql";
import { NewGuest } from "./GuestList/components/NewGuest";
import MemberHoldCounterSmall from "./MemberList/MemberHoldCounter.small";
import { MemberList, MemberListQueryString } from "./MemberList/MemberList";
import { MemberListQuery } from "./MemberList/__generated__/MemberListQuery.graphql";

const [MEMBER, GUEST, CUSTOMER] = [0, 1, 2];

export const AddPatrons = ({
  search,
  setSearch,
  currentPatron,
  isGroupFullOfGuests,
  isGroupFullOfMembers,
  isGroupFullOfAttendees,
  checkNewDisabled,
  areGuestsAllowed = true,
  areMembersAllowed = true,
  canAddNewGuest,
  handleAddMemberClick,
  handleAddGuestClick,
  handleAddCustomerClick,
  handleAddUnknownCustomerClick,
  memberHoldEnabled,
  showGuestListOverlay,
  unknownGuestLimit,
  onNewGuestSubmission,
  onAddUnknownGuest,
  onRemoveUnknownGuest,
  onRemoveUnknownCustomer,
  memberHoldLimit,
  memberListHeader,
  guestListHeader,
  onAddMemberHold,
  onRemoveMemberHold,
  bg,
  players,
  allPlayers,
  disabledPlayerIds = [],
  selectedPatrons = [],
  unknownGuestCount,
  memberHoldCount,
  unknownGuestsEnabled,
  isAddingNewGuest,
  setIsAddingNewGuest,
  eventIdToExcludeParticipantsFromResults,
  includeTransportationType,
  supportsPublic,
  isGroupFullOfCustomers,
  unknownCustomerEnabled,
  playerNounOverride,
  onRemovePlayer,
  keepItemInListAfterCheck = false,
  selectionAction,
}: AddPatronsInternalProps) => {
  const unknownGuestCountValue = unknownGuestCount ?? 0;
  const memberHoldCountValue = memberHoldCount ?? 0;
  const debouncedSearch = useDebouncedSearch(search);
  const shouldKeepUnknownGuestCounterVisible =
    keepItemInListAfterCheck &&
    isGroupFullOfGuests &&
    unknownGuestCountValue > 0 &&
    !isGroupFullOfAttendees &&
    areGuestsAllowed;
  const shouldKeepMemberHoldCounterVisible =
    keepItemInListAfterCheck &&
    isGroupFullOfMembers &&
    memberHoldCountValue > 0 &&
    !isGroupFullOfAttendees &&
    areMembersAllowed;

  const unknownGuestMax = isGroupFullOfGuests
    ? unknownGuestCountValue
    : unknownGuestLimit;

  const unknownCustomerMax = isGroupFullOfCustomers
    ? unknownGuestCountValue
    : unknownGuestLimit;

  const memberHoldMax = isGroupFullOfMembers
    ? memberHoldCountValue
    : memberHoldLimit;

  const [guestListQueryRef, loadGuestListQuery] =
    useQueryLoader<GuestListQuery>(GuestListQueryString);
  const [memberListQueryRef, loadMemberListQuery] =
    useQueryLoader<MemberListQuery>(MemberListQueryString);
  const [customerListQueryRef, loadCustomerListQuery] =
    useQueryLoader<CustomerListQuery>(CustomerListQueryString);

  const currentSlotId = useAtomValue(currentAddPatronsSlotIdAtom);
  const selectedPlayerIdKey = players.filter(Boolean).join(",");
  const customerQueryPlayerIds = useMemo(() => {
    if (keepItemInListAfterCheck) {
      return [];
    }

    if (selectedPlayerIdKey) {
      return selectedPlayerIdKey.split(",");
    }

    return currentPatron?.playerId ? [currentPatron.playerId] : [];
  }, [currentPatron?.playerId, keepItemInListAfterCheck, selectedPlayerIdKey]);

  const initialAddPlayerMode = supportsPublic
    ? CUSTOMER
    : areGuestsAllowed && !areMembersAllowed
    ? GUEST
    : MEMBER;

  const [addPlayerMode, setAddPlayerMode] = useState(initialAddPlayerMode);
  const [requestedAddPlayerMode, setRequestedAddPlayerMode] =
    useState(initialAddPlayerMode);

  const handleSetAddPlayerMode = (index: number) => {
    if (isAddingNewGuest && index !== GUEST) {
      setIsAddingNewGuest?.(false);
    }

    setRequestedAddPlayerMode(index);
    setAddPlayerMode(index);
  };

  useEffect(() => {
    if (supportsPublic) {
      setAddPlayerMode(CUSTOMER);
    } else if (isGroupFullOfMembers && !isGroupFullOfGuests) {
      setAddPlayerMode(GUEST);
    } else if (isGroupFullOfGuests && !isGroupFullOfMembers) {
      setAddPlayerMode(MEMBER);
    }
  }, [isGroupFullOfMembers, isGroupFullOfGuests, supportsPublic]);

  const displayedAddPlayerMode = supportsPublic
    ? CUSTOMER
    : shouldKeepUnknownGuestCounterVisible && requestedAddPlayerMode === GUEST
    ? GUEST
    : shouldKeepMemberHoldCounterVisible && requestedAddPlayerMode === MEMBER
    ? MEMBER
    : addPlayerMode;

  const isGuestListCheckNewDisabled =
    checkNewDisabled ||
    isGroupFullOfGuests ||
    isGroupFullOfAttendees ||
    !areGuestsAllowed;

  const hasReachedAttendeesLimit =
    isGroupFullOfAttendees &&
    (displayedAddPlayerMode === MEMBER ||
      displayedAddPlayerMode === GUEST ||
      displayedAddPlayerMode === CUSTOMER);

  const closeAddNewGuest = (options?: { clearSearch?: boolean }) => {
    if (options?.clearSearch) {
      setSearch("");
    }

    setIsAddingNewGuest?.(false);
  };

  /**
   * Handle firing the query when we search etc.
   */
  useEffect(() => {
    if (displayedAddPlayerMode === GUEST) {
      loadGuestListQuery(
        {
          slotId: currentSlotId,
          search: debouncedSearch,
          players: currentPatron?.playerId ? [currentPatron.playerId] : [],
        },
        { fetchPolicy: "network-only" }
      );
    } else if (displayedAddPlayerMode === MEMBER) {
      loadMemberListQuery(
        {
          slotId: currentSlotId,
          includeTransportationType,
          search: debouncedSearch,
          players: currentPatron?.playerId ? [currentPatron.playerId] : [],
          eventIdToExcludeParticipantsFromResults:
            eventIdToExcludeParticipantsFromResults,
        },
        { fetchPolicy: "store-and-network" }
      );
    } else if (displayedAddPlayerMode === CUSTOMER) {
      loadCustomerListQuery({
        slotId: currentSlotId,
        includeTransportationType,
        search: debouncedSearch,
        players: customerQueryPlayerIds,
        eventIdToExcludeParticipantsFromResults:
          eventIdToExcludeParticipantsFromResults,
      });
    }
  }, [
    currentPatron?.playerId,
    currentSlotId,
    displayedAddPlayerMode,
    eventIdToExcludeParticipantsFromResults,
    includeTransportationType,
    loadGuestListQuery,
    loadMemberListQuery,
    loadCustomerListQuery,
    customerQueryPlayerIds,
    debouncedSearch,
  ]);

  // Capacity warning banners scroll WITH the list (composed into each list's
  // ListHeaderComponent) so they never clip the pinned tabs/search when the
  // keyboard is up. See ENG-2362.
  const chromeHeader = (
    <>
      {hasReachedAttendeesLimit ? (
        <Box px={6} pb={4}>
          <InfoBanner
            message={t`You've reached the maximum attendee limit`}
            colorTheme="WARNING"
          />
        </Box>
      ) : null}
      {(checkNewDisabled || isGroupFullOfMembers) &&
      !isGroupFullOfAttendees &&
      displayedAddPlayerMode === MEMBER ? (
        <Box px={6} pb={4}>
          <InfoBanner
            message={t`You've reached the maximum number of members`}
            colorTheme="WARNING"
          />
        </Box>
      ) : null}
      {(checkNewDisabled || isGroupFullOfGuests) &&
      !isGroupFullOfAttendees &&
      displayedAddPlayerMode === GUEST ? (
        <Box px={6} pb={4}>
          <InfoBanner
            message={t`You've reached the maximum number of guests`}
            colorTheme="WARNING"
          />
        </Box>
      ) : null}
      {displayedAddPlayerMode === CUSTOMER && isGroupFullOfCustomers ? (
        <Box px={6} pb={4}>
          <InfoBanner
            message={t`You've reached the maximum number of players`}
            colorTheme="WARNING"
          />
        </Box>
      ) : null}
    </>
  );

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === "ios" ? "padding" : undefined}
      style={{ flex: 1 }}
    >
      {/*
        Single scroll surface: the FlashList inside each list IS the only
        vertical scroller. Tabs + search stay pinned above it; the search
        TextInput must stay out of the virtualized list so it doesn't lose
        focus on each keystroke. See ENG-2362.
      */}
      {supportsPublic ? null : (
        <Box width="100%" bg={bg} pb={4}>
          <UnderlinedTabs
            tabs={[t`Member`, t`Guest`]}
            activeTabIndex={displayedAddPlayerMode}
            setActiveTabIndex={handleSetAddPlayerMode}
            tabStyle={{ flexShrink: 0, flexGrow: 1, flex: 1 }}
          />
        </Box>
      )}
      {isAddingNewGuest ? null : (
        <Box py={supportsPublic ? 4 : 0} testID="add-people-search-section">
          <AddPeopleSearchbox search={search} setSearch={setSearch} />
        </Box>
      )}

      <Box flex={1} bg={bg}>
        <Suspense fallback={<PlayersListSkeleton px={6} />}>
          <>
            {displayedAddPlayerMode === MEMBER && memberListQueryRef ? (
              <MemberList
                memberQueryRef={memberListQueryRef}
                onCheckPlayer={handleAddMemberClick}
                onRemovePlayer={onRemovePlayer}
                checkNewDisabled={
                  checkNewDisabled ||
                  isGroupFullOfMembers ||
                  isGroupFullOfAttendees ||
                  !areMembersAllowed
                }
                bg={bg}
                eventIdToExcludeParticipantsFromResults={
                  eventIdToExcludeParticipantsFromResults
                }
                ListHeaderComponent={
                  <>
                    {chromeHeader}
                    {memberListHeader || (
                      <MemberHoldCounterSmall
                        memberHoldEnabled={memberHoldEnabled}
                        checkNewDisabled={checkNewDisabled}
                        isDecrementDisabled={
                          keepItemInListAfterCheck && memberHoldCountValue <= 0
                        }
                        isIncrementDisabled={
                          keepItemInListAfterCheck &&
                          (isGroupFullOfMembers || hasReachedAttendeesLimit)
                        }
                        memberHoldLimit={memberHoldMax}
                        memberHoldCount={memberHoldCountValue}
                        onAddMemberHold={onAddMemberHold}
                        onRemoveMemberHold={onRemoveMemberHold}
                        hasReachedAttendeesLimit={hasReachedAttendeesLimit}
                      />
                    )}
                  </>
                }
                players={players}
                disabledPlayerIds={disabledPlayerIds}
                selectedPatrons={selectedPatrons.filter(
                  (patron) => patron.type === "Member"
                )}
                currentPatron={currentPatron}
                keepItemInListAfterCheck={keepItemInListAfterCheck}
                selectionAction={selectionAction}
              />
            ) : null}

            {displayedAddPlayerMode === GUEST ? (
              showGuestListOverlay ? (
                // Non-list branch: no FlashList here, so a light ScrollView
                // keeps this reachable when the keyboard is up. See ENG-2362.
                <ScrollView
                  style={{ flex: 1 }}
                  keyboardShouldPersistTaps="handled"
                >
                  <Box flex={1} alignItems="center" px={6} py={2}>
                    <InfoBanner
                      message={t`In order to add a guest, there must be at least one member in the booking`}
                      colorTheme="ERROR"
                    />
                  </Box>
                </ScrollView>
              ) : isAddingNewGuest && onNewGuestSubmission ? (
                // Non-list branch: the New Guest form has its own inputs that
                // must stay reachable with the keyboard up. See ENG-2362.
                <ScrollView
                  style={{ flex: 1 }}
                  keyboardShouldPersistTaps="handled"
                >
                  <NewGuest
                    placeholder={search}
                    onNewGuestSubmission={onNewGuestSubmission}
                    closeAddNewGuest={closeAddNewGuest}
                  />
                </ScrollView>
              ) : (
                <>
                  {guestListQueryRef ? (
                    <GuestList
                      currentPatron={currentPatron}
                      guestQueryRef={guestListQueryRef}
                      onCheckPlayer={handleAddGuestClick}
                      onRemovePlayer={onRemovePlayer}
                      checkNewDisabled={isGuestListCheckNewDisabled}
                      players={players}
                      allPlayers={allPlayers}
                      disabledPlayerIds={disabledPlayerIds}
                      ListHeaderComponent={
                        <>
                          {chromeHeader}
                          {guestListHeader || (
                            <GuestListHeader
                              onAddUnknownGuest={onAddUnknownGuest}
                              onRemoveUnknownGuest={onRemoveUnknownGuest}
                              unknownGuestLimit={unknownGuestMax}
                              unknownGuestCount={unknownGuestCountValue}
                              isDecrementDisabled={
                                keepItemInListAfterCheck &&
                                unknownGuestCountValue <= 0
                              }
                              isIncrementDisabled={
                                keepItemInListAfterCheck &&
                                (isGroupFullOfGuests ||
                                  hasReachedAttendeesLimit)
                              }
                              unknownGuestEnabled={
                                unknownGuestsEnabled &&
                                !isGroupFullOfAttendees &&
                                areGuestsAllowed &&
                                (!isGroupFullOfGuests ||
                                  shouldKeepUnknownGuestCounterVisible)
                              }
                              onCheckPlayer={handleAddGuestClick}
                              addedPatrons={[]}
                            />
                          )}
                        </>
                      }
                      bg={bg}
                      ListFooterComponent={
                        canAddNewGuest &&
                        !isGroupFullOfGuests &&
                        !isGroupFullOfAttendees ? (
                          <Box
                            style={{ paddingBottom: 120 }}
                            width="100%"
                            px={6}
                          >
                            <Button
                              accessibilityLabel={t`Add New Guest button`}
                              width="100%"
                              variant="secondary"
                              key={"guest-list-add-guest-button"}
                              onPress={() => {
                                setIsAddingNewGuest?.(true);
                              }}
                              isDisabled={checkNewDisabled}
                              testID={"add-new-player-btn"}
                              leftIcon={<PersonPlusIcon />}
                            >
                              {t`Add New Guest`}
                            </Button>
                          </Box>
                        ) : (
                          <Box height={128}></Box>
                        )
                      }
                      keepItemInListAfterCheck={keepItemInListAfterCheck}
                      selectionAction={selectionAction}
                    />
                  ) : null}
                </>
              )
            ) : null}

            {displayedAddPlayerMode === CUSTOMER && customerListQueryRef ? (
              <CustomerList
                disabledPlayerIds={disabledPlayerIds}
                customerQueryRef={customerListQueryRef}
                onCheckPlayer={handleAddCustomerClick ?? (() => {})}
                onRemovePlayer={onRemovePlayer}
                onAddUnknownCustomerClick={handleAddUnknownCustomerClick}
                bg={bg}
                players={players}
                selectedPatrons={selectedPatrons.filter(
                  (patron) => patron.type === "Customer"
                )}
                currentPatron={currentPatron}
                checkNewDisabled={checkNewDisabled || isGroupFullOfCustomers}
                unknownCustomerEnabled={unknownCustomerEnabled}
                ListHeaderComponent={
                  <>
                    {chromeHeader}
                    {unknownCustomerEnabled ? (
                      <UnknownCustomerCounter
                        checkNewDisabled={checkNewDisabled}
                        isDecrementDisabled={
                          keepItemInListAfterCheck &&
                          unknownGuestCountValue <= 0
                        }
                        isIncrementDisabled={
                          keepItemInListAfterCheck &&
                          (isGroupFullOfCustomers || hasReachedAttendeesLimit)
                        }
                        unknownCustomerCount={unknownGuestCountValue}
                        onAddUnknownCustomer={handleAddUnknownCustomerClick}
                        onRemoveUnknownCustomer={onRemoveUnknownCustomer}
                        unknownCustomerLimit={unknownCustomerMax}
                        playerNounOverride={playerNounOverride}
                      />
                    ) : null}
                  </>
                }
                keepItemInListAfterCheck={keepItemInListAfterCheck}
                selectionAction={selectionAction}
              />
            ) : null}
          </>
        </Suspense>
      </Box>
    </KeyboardAvoidingView>
  );
};
